import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Snackbar
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  Send as SendIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from '../../commons/useTranslasion';
import { useDateFormatters } from '../../utils/useDateFormatters';
import { useSolicitacaoActions } from '../../states/solicitacoes/solicitacao.actions';
import { IAnexoSolicitacao, IComentarioRequest, ISolicitacao } from '../../commons/types/solicitacoes';
import { useRecoilValue } from 'recoil';
import { authAtom } from '../../states/usuarios/AuthState';

const DetalhesSolicitacao: React.FC = () => {
  const { translate } = useTranslation();
  const { formatarDataHora } = useDateFormatters();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const solicitacaoActions = useSolicitacaoActions();
  const authState = useRecoilValue(authAtom);
  
  const [solicitacao, setSolicitacao] = useState<ISolicitacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comentario, setComentario] = useState('');
  const [anexosComentario, setAnexosComentario] = useState<File[]>([]);
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    carregarSolicitacao();
  }, [id]);

  const carregarSolicitacao = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await solicitacaoActions.obterSolicitacao(parseInt(id));
      setSolicitacao(response);
      setError(null);
    } catch (err) {
      setError(typeof err === 'string' ? err : translate('erroAoBuscarSolicitacoes'));
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    history.push('/solicitacoes');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileList = Array.from(e.target.files);
      
      // Validar tamanho dos arquivos (máximo 10MB cada)
      const invalidFiles = fileList.filter(file => file.size > 10 * 1024 * 1024);
      if (invalidFiles.length > 0) {
        setSnackbarMessage(translate('tamanhoMaximoArquivo'));
        setSnackbarOpen(true);
        return;
      }
      
      // Validar quantidade de arquivos (máximo 5)
      if (anexosComentario.length + fileList.length > 5) {
        setSnackbarMessage(translate('limiteArquivos'));
        setSnackbarOpen(true);
        return;
      }
      
      setAnexosComentario(prev => [...prev, ...fileList]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setAnexosComentario(prev => prev.filter((_, i) => i !== index));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEnviarComentario = async () => {
    if (!comentario.trim() || !id) return;

    setEnviandoComentario(true);
    
    try {
      // Obter ID do assessorado do usuário autenticado
      let assessoradoId: number;
      
      try {
        assessoradoId = solicitacaoActions.getAssessoradoId();
      } catch (error) {
        setSnackbarMessage(typeof error === 'string' ? error : 'Não foi possível obter informações do usuário');
        setSnackbarOpen(true);
        setEnviandoComentario(false);
        return;
      }
      
      const comentarioData: IComentarioRequest = {
        conteudo: comentario.trim(),
        solicitacaoId: parseInt(id),
        assessoradoId,
        anexos: anexosComentario.length > 0 ? anexosComentario : undefined
      };
      
      await solicitacaoActions.adicionarComentario(comentarioData);
      
      setComentario('');
      setAnexosComentario([]);
      setSnackbarMessage(translate('comentarioAdicionadoComSucesso'));
      setSnackbarOpen(true);
      
      // Recarregar solicitação para mostrar o novo comentário
      await carregarSolicitacao();
    } catch (error) {
      setSnackbarMessage(typeof error === 'string' ? error : translate('erroAoAdicionarComentario'));
      setSnackbarOpen(true);
    } finally {
      setEnviandoComentario(false);
    }
  };

  const formatarData = (dataString: string) => {
    try {
      return formatarDataHora(dataString);
    } catch (e) {
      return dataString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'backlog':
        return '#9e9e9e';
      case 'em andamento':
        return '#2196f3';
      case 'concluído':
        return '#4caf50';
      case 'cancelado':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const getFileIcon = (tipo: string) => {
    if (tipo.includes('image')) return '🖼️';
    if (tipo.includes('pdf')) return '📄';
    if (tipo.includes('word') || tipo.includes('document')) return '📝';
    if (tipo.includes('excel') || tipo.includes('sheet')) return '📊';
    if (tipo.includes('zip') || tipo.includes('rar')) return '🗜️';
    return '📎';
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleVoltar} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {translate('detalhesSolicitacao')}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{translate('carregandoDetalhes')}</Typography>
          </Box>
        ) : error ? (
          <Paper sx={{ p: 3, bgcolor: '#fff3f3', color: '#d32f2f', mt: 2 }}>
            <Typography>{error}</Typography>
          </Paper>
        ) : solicitacao ? (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" component="h2">
                    {solicitacao.titulo}
                  </Typography>
                  <Chip 
                    label={solicitacao.status} 
                    sx={{ 
                      bgcolor: getStatusColor(solicitacao.status) + '20',
                      color: getStatusColor(solicitacao.status),
                      fontWeight: 'bold'
                    }} 
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{translate('dataRegistro')}:</strong> {formatarData(solicitacao.dataCriacao)}
                  </Typography>
                </Box>
                
                <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#fafafa' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {solicitacao.descricao}
                  </Typography>
                </Paper>
                
                {solicitacao.anexos && solicitacao.anexos.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {translate('anexos')}:
                    </Typography>
                    <List dense>
                      {solicitacao.anexos.map((anexo) => (
                        <ListItem 
                          key={anexo.id}
                          component={Paper}
                          variant="outlined"
                          sx={{ mb: 1, borderRadius: 1 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Typography>{getFileIcon(anexo.tipo)}</Typography>
                          </ListItemIcon>
                          <ListItemText 
                            primary={anexo.nome} 
                            secondary={anexo.tipo} 
                          />
                          <Button 
                            size="small" 
                            component={Link} 
                            href={anexo.url} 
                            target="_blank"
                            download
                          >
                            {translate('baixar')}
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                
                <Divider sx={{ my: 4 }} />
                
                <Typography variant="h6" gutterBottom>
                  {translate('comentarios')}
                </Typography>
                
                {solicitacao.comentarios && solicitacao.comentarios.length > 0 ? (
                  <Box sx={{ mb: 4 }}>
                    {solicitacao.comentarios.map((comentario) => (
                      <Card key={comentario.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                mr: 1,
                                bgcolor: comentario.usuario.id === authState?.id ? 'primary.main' : 'secondary.main'
                              }}
                            >
                              {comentario.usuario.nome.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {comentario.usuario.nome}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatarData(comentario.dataCriacao)}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                            {comentario.comentario}
                          </Typography>
                          
                          {comentario.anexos && comentario.anexos.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" display="block" gutterBottom>
                                {translate('anexos')}:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {comentario.anexos.map((anexo: IAnexoSolicitacao) => (
                                  <Chip
                                    key={anexo.id}
                                    icon={<FileIcon fontSize="small" />}
                                    label={anexo.nome}
                                    size="small"
                                    variant="outlined"
                                    component={Link}
                                    href={anexo.url}
                                    target="_blank"
                                    clickable
                                    sx={{ maxWidth: '100%', textOverflow: 'ellipsis' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#f5f5f5', borderRadius: 1, mb: 3 }}>
                    <Typography color="text.secondary">
                      {translate('semComentarios')}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={translate('escreverComentario')}
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    disabled={enviandoComentario}
                    variant="outlined"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <input
                      type="file"
                      id="comment-file-upload"
                      multiple
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      disabled={enviandoComentario || anexosComentario.length >= 5}
                    />
                    <label htmlFor="comment-file-upload">
                      <Button
                        component="span"
                        startIcon={<AttachFileIcon />}
                        disabled={enviandoComentario || anexosComentario.length >= 5}
                        size="small"
                      >
                        {translate('anexarArquivos')}
                      </Button>
                    </label>
                    
                    {anexosComentario.length > 0 && (
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography variant="caption" display="block" gutterBottom>
                          {translate('anexos')} ({anexosComentario.length}/5):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {anexosComentario.map((file, index) => (
                            <Chip
                              key={index}
                              label={file.name}
                              size="small"
                              onDelete={() => handleRemoveFile(index)}
                              disabled={enviandoComentario}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={enviandoComentario ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={handleEnviarComentario}
                    disabled={enviandoComentario || !comentario.trim()}
                  >
                    {translate('enviarComentario')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </Paper>
      </Box>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default DetalhesSolicitacao;
